import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface ReportNotification {
  id: string;
  post_id: string;
  reporter_id: string;
  reason: string;
  description: string | null;
  status: string;
  created_at: string;
  reporter_name: string | null;
  post_caption: string | null;
  pet_name: string | null;
}

export const useModerationNotifications = (userRole: string | null) => {
  const [pendingCount, setPendingCount] = useState(0);
  const [latestReport, setLatestReport] = useState<ReportNotification | null>(null);

  // Only fetch if user is admin or moderator
  const { data: reportsData, refetch } = useQuery({
    queryKey: ['moderation-notifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('post_reports')
        .select('id, post_id, reporter_id, reason, description, status, created_at')
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Enrich with user and post data for pending reports
      const enrichedReports = await Promise.all(
        (data || []).map(async (report) => {
          // Get reporter name
          const { data: reporter } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', report.reporter_id)
            .maybeSingle();

          // Get post and pet info
          const { data: post } = await supabase
            .from('memory_posts')
            .select(`
              caption,
              pets(name)
            `)
            .eq('id', report.post_id)
            .maybeSingle();

          return {
            ...report,
            reporter_name: reporter?.full_name || null,
            post_caption: post?.caption || null,
            pet_name: (post as any)?.pets?.name || null,
          } as ReportNotification;
        })
      );

      return enrichedReports;
    },
    enabled: userRole === 'admin' || userRole === 'moderator',
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  // Set up real-time subscription
  useEffect(() => {
    if (userRole !== 'admin' && userRole !== 'moderator') return;

    const channel = supabase
      .channel('reports-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'post_reports'
        },
        async (payload) => {
          console.log('New report received:', payload);
          const newReport = payload.new as ReportNotification;
          
          // Get additional data for the notification
          const { data: reporter } = await supabase
            .from('users')
            .select('full_name')
            .eq('id', newReport.reporter_id)
            .maybeSingle();

          const enrichedReport = {
            ...newReport,
            reporter_name: reporter?.full_name || 'Anonymous User'
          };
          
          // Show toast notification
          toast.error('New Report Submitted', {
            description: `New report: ${newReport.reason} by ${enrichedReport.reporter_name}`,
            action: {
              label: 'View',
              onClick: () => window.location.href = '/moderation'
            }
          });

          // Update latest report
          setLatestReport(enrichedReport);
          
          // Refetch data to update count
          refetch();
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'post_reports'
        },
        () => {
          // Refetch when report status changes
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userRole, refetch]);

  // Update pending count when data changes
  useEffect(() => {
    if (reportsData) {
      setPendingCount(reportsData.length);
    }
  }, [reportsData]);

  return {
    pendingCount,
    latestReport,
    reportsData: reportsData || [],
    refetch
  };
};